import React, { useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { BottomNavigation, BottomNavigationAction, Box, AppBar, Toolbar, Typography } from '@mui/material';
import { Home, Star, AccountCircle, MenuBook } from '@mui/icons-material';
import {theme} from '../theme/theme';


// Screens
// 导入HomeScreen组件，并指定其类型为React.ComponentType
import HomeScreen from '../screens/main/HomeScreen';
import AstrolabeScreen from '../screens/main/AstrolabeScreen';
import ConsultationScreen from '../screens/main/ConsultationScreen';
import ProfileScreen from '../screens/main/ProfileScreen';
import ConsultationHistoryScreen from '../screens/main/ConsultationHistoryScreen';
import IntroductionScreen from '../screens/main/IntroductionScreen';
import SoulmatePaintingScreen from '../screens/main/SoulmatePaintingScreen';
import ReportsScreen from '../screens/main/ReportsScreen';
// 订阅管理暂未接入，移除订阅管理入口
import FeaturesScreen from '../screens/FeaturesScreen';


const AppHeader = ({ title }: { title: string }) => (
  <AppBar position="static" sx={{ backgroundColor: theme.colors.primary, borderRadius: 0 }}>
    <Toolbar>
      <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#FFFFFF', fontWeight: 'bold' }}>
        {title}
      </Typography>
    </Toolbar>
  </AppBar>
);

const getPageTitle = (pathname: string) => {
  switch (pathname) {
    case '/':
    case '/introduction':
      return '功能介绍';
    case '/home':
      return '首页';
    case '/astrolabe':
      return '星盘';
    case '/consultation':
      return '咨询';
    case '/consultation/history':
      return '咨询历史';
    case '/soulmate-painting':
      return '灵魂伴侣画像';
    case '/reports':
      return '报告';
    case '/profile':
      return '个人资料';
    // 订阅管理暂未接入
    case '/features':
      return '功能介绍';
    default:
      return '紫微AI';
  }
};

const MainNavigator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = useState(() => {
    const path = location.pathname;
    if (path.startsWith('/introduction')) return 0;
    if (path.startsWith('/home')) return 1;
    if (path.startsWith('/astrolabe')) return 2;
    if (path.startsWith('/profile')) return 3;
    // 订阅管理暂未接入
    return 0;
  });

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    switch (newValue) {
      case 0:
        navigate('/introduction');
        break;
      case 1:
        navigate('/home');
        break;
      case 2:
        navigate('/astrolabe');
        break;
      case 3:
        navigate('/profile');
        break;
      // 订阅管理暂未接入
      default:
        navigate('/introduction');
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh',
      width: '100%',
      overflow: 'hidden'
    }}>
      <AppHeader title={getPageTitle(location.pathname)} />
      
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch', // iOS 平滑滚动
        paddingBottom: { xs: '60px', sm: '60px' }, // 为底部导航留出空间
      }}>
        <Routes>
          <Route path="/" element={<Navigate to="/introduction" replace />} />
          <Route path="/introduction" element={<IntroductionScreen />} />
          <Route path="/home" element={<HomeScreen />} />
          <Route path="/astrolabe" element={<AstrolabeScreen />} />
          <Route path="/consultation" element={<ConsultationScreen />} />
          <Route path="/consultation/history" element={<ConsultationHistoryScreen />} />
          <Route path="/soulmate-painting" element={<SoulmatePaintingScreen />} />
          <Route path="/reports" element={<ReportsScreen />} />
          <Route path="/profile" element={<ProfileScreen />} />
          {/* 订阅管理暂未接入，移除相关路由 */}
          <Route path="/features" element={<FeaturesScreen />} />
          <Route path="/auth/*" element={<Navigate to="/introduction" replace />} />
          <Route path="*" element={<Navigate to="/introduction" replace />} />
        </Routes>
      </Box>
      
      <BottomNavigation
        value={value}
        onChange={handleChange}
        sx={{
          backgroundColor: theme.colors.surface,
          borderTop: `1px solid ${theme.colors.primary}`,
          height: { xs: 60, sm: 60 },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <BottomNavigationAction
          label="功能介绍"
          icon={<MenuBook />}
          sx={{
            color: value === 0 ? theme.colors.primary : theme.colors.placeholder,
            '&.Mui-selected': { color: theme.colors.primary },
            minWidth: { xs: 'auto', sm: 80 },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            '& .MuiBottomNavigationAction-label': {
              fontSize: { xs: '0.6rem', sm: '0.75rem' },
              marginTop: { xs: '2px', sm: '4px' }
            }
          }}
        />
        <BottomNavigationAction
          label="首页"
          icon={<Home />}
          sx={{
            color: value === 1 ? theme.colors.primary : theme.colors.placeholder,
            '&.Mui-selected': { color: theme.colors.primary },
            minWidth: { xs: 'auto', sm: 80 },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            '& .MuiBottomNavigationAction-label': {
              fontSize: { xs: '0.6rem', sm: '0.75rem' },
              marginTop: { xs: '2px', sm: '4px' }
            }
          }}
        />
        <BottomNavigationAction
          label="星盘"
          icon={<Star />}
          sx={{
            color: value === 2 ? theme.colors.primary : theme.colors.placeholder,
            '&.Mui-selected': { color: theme.colors.primary },
            minWidth: { xs: 'auto', sm: 80 },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            '& .MuiBottomNavigationAction-label': {
              fontSize: { xs: '0.6rem', sm: '0.75rem' },
              marginTop: { xs: '2px', sm: '4px' }
            }
          }}
        />
        <BottomNavigationAction
          label="个人资料"
          icon={<AccountCircle />}
          sx={{
            color: value === 3 ? theme.colors.primary : theme.colors.placeholder,
            '&.Mui-selected': { color: theme.colors.primary },
            minWidth: { xs: 'auto', sm: 80 },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            '& .MuiBottomNavigationAction-label': {
              fontSize: { xs: '0.6rem', sm: '0.75rem' },
              marginTop: { xs: '2px', sm: '4px' }
            }
          }}
        />
        {/* 订阅管理暂未接入，移除底部订阅入口 */}
      </BottomNavigation>
    </Box>
  );
};

export default MainNavigator;